// src/services/FrontendService.js


import { makeLogger } from '@/services/utils/logger';
import { LOGGING } from '@/config';
import { translate } from '@/i18n';
const DEBUG_LOG = false;
const log = makeLogger('FrontendService', DEBUG_LOG, LOGGING.FUNC_SERVICES_LOG);
const THEME_STORAGE_KEY = 'app.theme';
const THEME_MODES = ['light', 'bright', 'contrast', 'dark'];
const THEME_CHANGED_EVENT = 'frontend-theme-changed';

const FrontendService = {
  THEME_CHANGED_EVENT,

  /** Читает настройку или плоский набор настроек, проверяя типы по defaults. */
  loadSettings(key, defaults) {
    const normalize = (value, fallback) => {
      if (typeof fallback === 'string') return typeof value === 'string' || typeof value === 'number' ? String(value) : fallback;
      if (typeof fallback === 'number') return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
      if (typeof fallback === 'boolean') return typeof value === 'boolean' ? value : fallback;
      return fallback;
    };
    const fallback = defaults && typeof defaults === 'object' ? { ...defaults } : defaults;
    if (!key) return fallback;
    try {
      const raw = localStorage.getItem(key);
      if (raw === null) return fallback;
      // Строковые настройки сохраняют существующий формат без JSON-кавычек.
      if (typeof defaults === 'string') return raw || defaults;
      const saved = JSON.parse(raw);
      if (defaults && typeof defaults === 'object') {
        if (!saved || typeof saved !== 'object' || Array.isArray(saved)) return fallback;
        return Object.fromEntries(Object.entries(defaults).map(([name, value]) => [
          name, normalize(Object.hasOwn(saved, name) ? saved[name] : undefined, value),
        ]));
      }
      return normalize(saved, defaults);
    } catch (_) {
      return fallback;
    }
  },

  /** Сохранение необязательно: недоступность хранилища не мешает работе страницы. */
  saveSettings(key, values) {
    if (!key) return;
    try {
      localStorage.setItem(key, typeof values === 'string' ? values : JSON.stringify(values));
    } catch (_) { /* Настройки продолжают действовать в текущей вкладке. */ }
  },

  removeSettings(key) {
    if (!key) return;
    try {
      localStorage.removeItem(key);
    } catch (_) { /* Настройки продолжают действовать в текущей вкладке. */ }
  },

  /**
   * Возвращает доступные режимы темы интерфейса.
   * @returns {string[]}
   */
  getThemeModes() {
    return [...THEME_MODES];
  },

  /**
   * Проверяет, является ли значение поддерживаемым режимом темы.
   * @param {string|null|undefined} mode - Проверяемое значение.
   * @returns {boolean}
   */
  isValidThemeMode(mode) {
    return THEME_MODES.includes(mode);
  },

  /**
   * Возвращает текущий режим темы из localStorage или DOM.
   * @returns {'light'|'bright'|'contrast'|'dark'}
   */
  getThemeMode() {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (this.isValidThemeMode(stored)) return stored;

    const current = document.documentElement.getAttribute('data-app-theme');
    if (this.isValidThemeMode(current)) return current;

    return 'light';
  },

  /**
   * Применяет режим темы к DOM и сохраняет выбор пользователя.
   * @param {'light'|'bright'|'contrast'|'dark'} mode - Новый режим темы.
   * @returns {'light'|'bright'|'contrast'|'dark'}
   */
  setThemeMode(mode) {
    const normalizedMode = this.isValidThemeMode(mode) ? mode : 'light';
    localStorage.setItem(THEME_STORAGE_KEY, normalizedMode);
    document.documentElement.setAttribute('data-bs-theme', normalizedMode === 'dark' ? 'dark' : 'light');
    document.documentElement.setAttribute('data-app-theme', normalizedMode);
    window.dispatchEvent(new CustomEvent(THEME_CHANGED_EVENT, { detail: { mode: normalizedMode } }));
    return normalizedMode;
  },

  /**
   * Переключает тему на следующий режим.
   * @param {'light'|'bright'|'contrast'|'dark'} [currentMode] - Текущий режим, если уже известен компоненту.
   * @returns {'light'|'bright'|'contrast'|'dark'}
   */
  toggleThemeMode(currentMode = this.getThemeMode()) {
    const safeCurrentMode = this.isValidThemeMode(currentMode) ? currentMode : this.getThemeMode();
    const currentIndex = THEME_MODES.indexOf(safeCurrentMode);
    return this.setThemeMode(THEME_MODES[(currentIndex + 1) % THEME_MODES.length]);
  },

  /**
   * Возвращает CSS-класс иконки текущего режима темы.
   * @param {'light'|'bright'|'contrast'|'dark'} mode - Текущий режим темы.
   * @returns {string}
   */
  getThemeIconClass(mode) {
    if (mode === 'bright') return 'bi bi-brightness-high';
    if (mode === 'contrast') return 'bi bi-circle-half';
    if (mode === 'dark') return 'bi bi-moon';
    return 'bi bi-cup-hot';
  },

  /**
   * Возвращает доступную подпись для кнопки переключения темы.
   * @param {'light'|'bright'|'contrast'|'dark'} mode - Текущий режим темы.
   * @returns {string}
   */
  getThemeButtonLabel(mode) {
    return translate(`theme.${mode}`);
  },

  /**
   * Получает сохранённую вкладку турнира из localStorage.
   * @param {string} keyPrefix - Префикс ключа.
   * @param {string} tournamentId - ID турнира.
   * @param {string} [fallback='swiss'] - Значение по умолчанию.
   * @returns {string}
   */
  getTournamentTab(keyPrefix, tournamentId, fallback = 'swiss') {
    const value = localStorage.getItem(`${keyPrefix}_${tournamentId}`);
    return value || fallback;
  },

  /**
   * Сохраняет выбранную вкладку турнира в localStorage.
   * @param {string} keyPrefix - Префикс ключа.
   * @param {string} tournamentId - ID турнира.
   * @param {string} value - Сохраняемое значение.
   */
  setTournamentTab(keyPrefix, tournamentId, value) {
    localStorage.setItem(`${keyPrefix}_${tournamentId}`, value);
  },

  /**
   * Получает сгруппированные раунды из localStorage.
   * @param {string} tournamentId - ID турнира.
   * @returns {Array}
   */
  getGroupedRounds(tournamentId) {
    const raw = localStorage.getItem(`groupedRounds_${tournamentId}`);
    try {
      const parsed = raw ? JSON.parse(raw) : [];
      log('getGroupedRounds', '📤 Получены сохранённые группы:', parsed);
      return parsed;
    } catch (e) {
      log.error('getGroupedRounds', 'Ошибка при чтении groupedRounds:', e);
      return [];
    }
  },

  /**
   * Сохраняет сгруппированные раунды в localStorage.
   * @param {string} tournamentId - ID турнира.
   * @param {Array} grouped - Массив сгруппированных данных.
   */
  setGroupedRounds(tournamentId, grouped) {
    log('setGroupedRounds', '💾 Сохраняем группы:', grouped);
    localStorage.setItem(`groupedRounds_${tournamentId}`, JSON.stringify(grouped));
  }
};

export default FrontendService;
